import { useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Input, InputNumber, Modal, Select, Table, Tabs, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { BookOpen, GitCompareArrows, Pencil, Plus, Scale, TrendingUp } from 'lucide-react';
import Layout from '../../Layout';
import {
  accountName, BOOK, buildEntries, diffReport, inBook, inPeriod, isGuarded, plReport, SCOPE, scopeError, trialBalance,
  type Book, type Entry, type PlRow, type TrialRow,
} from './ktBooks';
import { ktApi, today, useKt } from './ktStore';
import { dmy, fmtMoney, type BookScope, type MappingRule } from './ktLogic';
import { DemoBar, Kpi, PageHeader, Panel, ScopeTag, showErrors } from './ktUi';

const SCOPES = (Object.keys(SCOPE) as BookScope[]).map(v => ({ value: v, label: SCOPE[v] }));
const money = (n: number) => (n < 0 ? `(${fmtMoney(-n)})` : fmtMoney(n));

/** Hai sổ trên cùng một nguồn chứng từ: bút toán theo bảng Mapping, P&L từng sổ và báo cáo giải trình chênh lệch (SRS 1.1) */
export default function KeToanBooks() {
  const s = useKt();
  const [ym, setYm] = useState(dayjs().subtract(1, 'month').format('YYYY-MM'));
  const [book, setBook] = useState<Book>('tax');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<MappingRule | null>(null);

  const entries = useMemo(() => buildEntries(s), [s]);
  const months = useMemo(() => Array.from(new Set(entries.map(e => e.date.slice(0, 7)))).sort().reverse(), [entries]);
  const pl = useMemo(() => plReport(entries, ym), [entries, ym]);
  const diff = useMemo(() => diffReport(entries, ym), [entries, ym]);
  const trial = useMemo(() => trialBalance(entries, book, ym), [entries, book, ym]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return entries.filter(e => inBook(e, book) && inPeriod(e, ym) && (!t || `${e.docNo} ${e.desc} ${e.debit} ${e.credit}`.toLowerCase().includes(t)));
  }, [entries, book, ym, q]);
  const gap = pl.profit.mgmt - pl.profit.tax;

  const plCols: ColumnsType<PlRow> = [
    { title: 'Chỉ tiêu', dataIndex: 'label' },
    { title: 'Sổ Thuế', key: 't', align: 'right', width: 170, render: (_, r) => money(r.tax) },
    { title: 'Sổ Quản trị', key: 'm', align: 'right', width: 170, render: (_, r) => money(r.mgmt) },
    { title: 'Chênh lệch', key: 'd', align: 'right', width: 150, render: (_, r) => (r.mgmt - r.tax ? <b>{money(r.mgmt - r.tax)}</b> : <span className="text-gray-400">0</span>) },
  ];
  const diffCols: ColumnsType<typeof diff.rows[number]> = [
    { title: 'Chứng từ', dataIndex: 'docNo', width: 100 },
    { title: 'Ngày', key: 'd', width: 110, render: (_, r) => dmy(r.date) },
    { title: 'Diễn giải', dataIndex: 'desc' },
    { title: 'Phạm vi sổ', key: 's', width: 140, render: (_, r) => <ScopeTag scope={r.scope} /> },
    { title: 'Lý do', dataIndex: 'reason', width: 240 },
    { title: 'Ảnh hưởng chênh lệch', key: 'e', align: 'right', width: 160, render: (_, r) => money(r.effect) },
  ];
  const entryCols: ColumnsType<Entry> = [
    { title: 'Ngày', key: 'd', width: 106, render: (_, e) => dmy(e.date) },
    { title: 'Chứng từ', key: 'n', width: 130, render: (_, e) => <div><div className="font-medium">{e.docNo}</div><div className="text-[11px] text-gray-500">{e.docKind}</div></div> },
    { title: 'Diễn giải', dataIndex: 'desc' },
    { title: 'Nợ', key: 'dr', width: 150, render: (_, e) => <Tooltip title={accountName(e.debit)}>{e.debit}</Tooltip> },
    { title: 'Có', key: 'cr', width: 150, render: (_, e) => <Tooltip title={accountName(e.credit)}>{e.credit}</Tooltip> },
    { title: 'Số tiền', key: 'a', align: 'right', width: 140, render: (_, e) => fmtMoney(e.amount) },
    { title: 'Phạm vi sổ', key: 's', width: 130, render: (_, e) => <ScopeTag scope={e.scope} /> },
  ];
  const trialCols: ColumnsType<TrialRow> = [
    { title: 'Tài khoản', dataIndex: 'account', width: 110 },
    { title: 'Tên', dataIndex: 'name' },
    { title: 'Phát sinh Nợ', key: 'd', align: 'right', width: 170, render: (_, r) => fmtMoney(r.debit) },
    { title: 'Phát sinh Có', key: 'c', align: 'right', width: 170, render: (_, r) => fmtMoney(r.credit) },
  ];
  const ruleCols: ColumnsType<MappingRule> = [
    { title: 'Nghiệp vụ', dataIndex: 'label' },
    { title: 'Nợ', key: 'd', width: 190, render: (_, r) => <span>{r.debit} <span className="text-[11px] text-gray-500">{accountName(r.debit)}</span></span> },
    { title: 'Có', key: 'c', width: 190, render: (_, r) => <span>{r.credit} <span className="text-[11px] text-gray-500">{accountName(r.credit)}</span></span> },
    { title: 'Phạm vi sổ', key: 's', width: 130, render: (_, r) => <ScopeTag scope={r.scope} /> },
    { title: '', key: 'a', width: 60, align: 'right', render: (_, r) => (
      <Tooltip title={s.tier === 'chief' ? 'Sửa' : 'Đổi vai Kế toán trưởng để sửa'}>
        <Button size="small" type="text" disabled={s.tier !== 'chief'} icon={<Pencil size={14} />} aria-label={`Sửa ${r.label}`} onClick={() => setEditing(r)} />
      </Tooltip>
    ) },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Sổ Thuế và Sổ Quản trị" desc="Một nguồn chứng từ, bút toán tự sinh theo bảng Mapping, chênh lệch hai sổ giải trình đến từng chứng từ"
          extra={(
            <>
              <Select value={ym} onChange={setYm} className="w-36" aria-label="Kỳ" options={months.map(m => ({ value: m, label: `Tháng ${m.slice(5)}/${m.slice(0, 4)}` }))} />
              {s.tier !== 'counter' && <Button type="primary" icon={<Plus size={14} />} onClick={() => setAdding(true)}>Lập chứng từ</Button>}
            </>
          )} />
        <DemoBar />
        <Alert type="warning" showIcon message="Ranh giới của mô hình 2 sổ"
          description="Sổ Thuế là sổ kế toán tài chính, ghi đủ mọi doanh thu và dòng tiền. Bút toán có tiền, công nợ, thuế hoặc doanh thu bắt buộc Cả 2 sổ. Sổ Quản trị chỉ khác ở bút toán nội bộ không phát sinh tiền như khấu hao theo shot, phân bổ theo kỳ. Tài khoản theo SRS, cần đối chiếu Thông tư 99/2025." />

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={<TrendingUp size={20} />} tone="bg-sky-50 text-sky-600" label="Lợi nhuận Sổ Thuế" value={money(pl.profit.tax)} />
          <Kpi icon={<TrendingUp size={20} />} tone="bg-emerald-50 text-emerald-600" label="Lợi nhuận Sổ Quản trị" value={money(pl.profit.mgmt)} />
          <Kpi icon={<GitCompareArrows size={20} />} tone="bg-amber-50 text-amber-600" label="Chênh lệch" value={money(gap)} hint={`${diff.rows.length} chứng từ giải trình`} />
          <Kpi icon={<Scale size={20} />} tone="bg-rose-50 text-rose-600" label="Kiểm tra khớp" value={gap === diff.total ? 'Khớp' : 'Lệch'} hint="Chênh lệch bằng tổng dòng giải trình" />
        </div>

        <Tabs items={[
          { key: 'pl', label: 'P&L hai sổ', children: (
            <div className="flex flex-col gap-4">
              <Panel title="Báo cáo P&L" icon={<BookOpen size={15} className="text-sky-600" />}>
                <Table rowKey="key" size="middle" columns={plCols} dataSource={pl.rows} pagination={false} scroll={{ x: 700 }}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><b>Lợi nhuận</b></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right"><b>{money(pl.profit.tax)}</b></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right"><b>{money(pl.profit.mgmt)}</b></Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right"><b>{money(gap)}</b></Table.Summary.Cell>
                    </Table.Summary.Row>
                  )} />
              </Panel>
              <Panel title={`Giải trình chênh lệch (${diff.rows.length} chứng từ)`} icon={<GitCompareArrows size={15} className="text-amber-600" />}>
                <Table rowKey="id" size="middle" columns={diffCols} dataSource={diff.rows} pagination={false} scroll={{ x: 1000 }} locale={{ emptyText: 'Kỳ này hai sổ không có chênh lệch' }} />
              </Panel>
            </div>
          ) },
          { key: 'entries', label: `Bút toán (${shown.length})`, children: (
            <Panel title={`Bút toán ${BOOK[book]}`} extra={(
              <div className="flex flex-wrap gap-2">
                <BookPick book={book} onChange={setBook} />
                <Input allowClear size="small" className="!w-52" placeholder="Tìm số chứng từ, tài khoản" aria-label="Tìm bút toán" value={q} onChange={e => setQ(e.target.value)} />
              </div>
            )}>
              <Table rowKey="id" size="small" columns={entryCols} dataSource={shown} pagination={{ pageSize: 15, showSizeChanger: false }} scroll={{ x: 1000 }} locale={{ emptyText: 'Không có bút toán' }} />
            </Panel>
          ) },
          { key: 'trial', label: 'Cân đối phát sinh', children: (
            <Panel title={`Cân đối phát sinh ${BOOK[book]}`} extra={<BookPick book={book} onChange={setBook} />}>
              <Table rowKey="account" size="middle" columns={trialCols} dataSource={trial} pagination={false} scroll={{ x: 700 }}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}><b>Tổng</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right"><b>{fmtMoney(trial.reduce((t, r) => t + r.debit, 0))}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right"><b>{fmtMoney(trial.reduce((t, r) => t + r.credit, 0))}</b></Table.Summary.Cell>
                  </Table.Summary.Row>
                )} />
            </Panel>
          ) },
          { key: 'rules', label: `Bảng Mapping (${s.rules.length})`, children: (
            <Panel title="Bảng quy tắc Mapping" extra={<span className="text-xs text-gray-500">Kế toán trưởng thiết lập; đổi quy tắc thì bút toán sinh lại</span>}>
              <Table rowKey="id" size="middle" columns={ruleCols} dataSource={s.rules} pagination={false} scroll={{ x: 900 }} />
            </Panel>
          ) },
        ]} />
      </div>
      {adding && <VoucherModal onClose={() => setAdding(false)} />}
      {editing && <RuleModal rule={editing} onClose={() => setEditing(null)} />}
    </Layout>
  );
}

const BookPick = ({ book, onChange }: { book: Book; onChange: (b: Book) => void }) => (
  <Select size="small" className="w-36" value={book} onChange={onChange} aria-label="Chọn sổ" options={(Object.keys(BOOK) as Book[]).map(b => ({ value: b, label: BOOK[b] }))} />
);

function VoucherModal({ onClose }: { onClose: () => void }) {
  const s = useKt();
  const rules = s.rules.filter(r => r.forVoucher);
  const [ruleId, setRuleId] = useState(rules[0]?.id);
  const rule = rules.find(r => r.id === ruleId);
  const [scope, setScope] = useState<BookScope>(rule?.scope || 'both');
  const [date, setDate] = useState<dayjs.Dayjs | null>(dayjs(today()));
  const [desc, setDesc] = useState('');
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [costCenter, setCostCenter] = useState('');
  const [nonDeductible, setNonDeductible] = useState('');
  const guard = rule ? scopeError(rule.debit, rule.credit, scope) : null;
  return (
    <Modal open title="Lập chứng từ" okText="Lưu" cancelText="Thôi" onCancel={onClose} destroyOnHidden width={560}
      onOk={() => {
        const r = ktApi.addVoucher({ date: date ? date.format('YYYY-MM-DD') : '', ruleId, desc, amount, scope, reason, costCenter, nonDeductible: nonDeductible.trim() || undefined });
        if (r.ok) { message.success('Đã lập chứng từ, bút toán đã sinh'); onClose(); } else showErrors(r.errors);
      }}>
      <div className="space-y-3">
        <div>
          <label htmlFor="kt-v-rule" className="text-sm font-medium">Loại chứng từ</label>
          <Select id="kt-v-rule" className="w-full" value={ruleId} options={rules.map(r => ({ value: r.id, label: `${r.label} (Nợ ${r.debit} / Có ${r.credit})` }))}
            onChange={v => { setRuleId(v); setScope(rules.find(r => r.id === v)?.scope || 'both'); }} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label htmlFor="kt-v-date" className="text-sm font-medium">Ngày</label><DatePicker id="kt-v-date" className="w-full" format="DD/MM/YYYY" value={date} onChange={setDate} /></div>
          <div><label htmlFor="kt-v-scope" className="text-sm font-medium">Phạm vi sổ</label><Select id="kt-v-scope" className="w-full" value={scope} onChange={setScope} options={SCOPES} /></div>
        </div>
        {guard && <Alert type="error" showIcon message={guard} />}
        <div><label htmlFor="kt-v-desc" className="text-sm font-medium">Diễn giải</label><Input id="kt-v-desc" value={desc} onChange={e => setDesc(e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="kt-v-amount" className="text-sm font-medium">Số tiền</label>
            <InputNumber id="kt-v-amount" className="!w-full" min={0} step={100000} value={amount} onChange={v => setAmount(Number(v) || 0)} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, ''))} />
          </div>
          <div><label htmlFor="kt-v-cc" className="text-sm font-medium">Mã trung tâm chi phí</label><Input id="kt-v-cc" value={costCenter} onChange={e => setCostCenter(e.target.value)} /></div>
        </div>
        {scope !== 'both' && <div><label htmlFor="kt-v-reason" className="text-sm font-medium">Lý do ghi riêng một sổ</label><Input id="kt-v-reason" value={reason} onChange={e => setReason(e.target.value)} /></div>}
        <div><label htmlFor="kt-v-nd" className="text-sm font-medium">Chi phí không được trừ (lý do, nếu có)</label><Input id="kt-v-nd" value={nonDeductible} onChange={e => setNonDeductible(e.target.value)} /></div>
      </div>
    </Modal>
  );
}

function RuleModal({ rule, onClose }: { rule: MappingRule; onClose: () => void }) {
  const [debit, setDebit] = useState(rule.debit);
  const [credit, setCredit] = useState(rule.credit);
  const [scope, setScope] = useState(rule.scope);
  const locked = isGuarded(debit) || isGuarded(credit);
  return (
    <Modal open title={`Quy tắc: ${rule.label}`} okText="Lưu" cancelText="Thôi" onCancel={onClose} destroyOnHidden
      onOk={() => { const r = ktApi.updateRule(rule.id, debit.trim(), credit.trim(), locked ? 'both' : scope); if (r.ok) { message.success('Đã lưu, bút toán đã sinh lại'); onClose(); } else showErrors(r.errors); }}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label htmlFor="kt-r-d" className="text-sm font-medium">Tài khoản Nợ</label><Input id="kt-r-d" value={debit} onChange={e => setDebit(e.target.value)} /></div>
          <div><label htmlFor="kt-r-c" className="text-sm font-medium">Tài khoản Có</label><Input id="kt-r-c" value={credit} onChange={e => setCredit(e.target.value)} /></div>
        </div>
        <div>
          <label htmlFor="kt-r-s" className="text-sm font-medium">Phạm vi sổ</label>
          <Select id="kt-r-s" className="w-full" disabled={locked} value={locked ? 'both' : scope} onChange={setScope} options={SCOPES} />
          {locked && <p className="text-xs text-gray-500 mt-1 mb-0">Tài khoản tiền, công nợ, thuế, doanh thu luôn ghi cả 2 sổ.</p>}
        </div>
      </div>
    </Modal>
  );
}
